/**
 * Adjusts the css top property of an element such that it sticks appropriately based on the scroll
 * direction. The container is assumed to be position: sticky, with top: 0 (or whatever). When
 * scrolling down, it'll stick to the bottom of the container, when scrolling up it'll stick to
 * the top of the container.
 */
if (!customElements.get('sticky-scroll-direction')) {
  class StickyScrollDirection extends HTMLElement {
    constructor() {
      super();

      this.connected = false;
      this.active = false;
      this.scrollFrame = null;
      this.stickyHeightTimer = null;
      this.mutationObserver = null;

      this.scrollListener = this.handleScroll.bind(this);
      this.stickyHeightChangeHandler = this.handleStickyHeightChange.bind(this);
      this.breakpointChangeHandler = this.handleBreakpointChange.bind(this);
      this.pageShowHandler = this.handlePageShow.bind(this);
      this.disclosureChangeHandler = this.handleDisclosureChange.bind(this);
      this.updateScrollPositionHandler = this.updateScrollPosition.bind(this);
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;

      this.init();
      window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      window.addEventListener('pageshow', this.pageShowHandler);
    }

    disconnectedCallback() {
      this.connected = false;
      window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      window.removeEventListener('pageshow', this.pageShowHandler);
      this.removeListeners();
    }

    init() {
      this.container = this.firstElementChild;
      this.minStickySize = this.dataset.minStickySize;
      this.toggleListeners();
    }

    /**
     * Either inits or destroys the component based on screen size.
     */
    toggleListeners() {
      const shouldBeActive = (this.minStickySize === 'lg' && theme.mediaMatches.lg)
        || (this.minStickySize === 'md' && theme.mediaMatches.md);

      if (shouldBeActive) {
        this.addListeners();
      } else {
        this.removeListeners();
      }
    }

    handleBreakpointChange() {
      const wasActive = this.active;
      this.toggleListeners();
      if (wasActive && this.active) this.handleScroll();
    }

    handlePageShow(evt) {
      if (!evt.persisted || !this.active) return;
      if (this.stickyHeightElems) this.setStickyHeight();
      this.handleScroll();
    }

    /**
     * Init the scroll watching.
     */
    addListeners() {
      if (this.active || !this.container || !this.parentElement) return;
      this.active = true;

      this.setInitialState();
      window.addEventListener('scroll', this.scrollListener, { passive: true });

      this.stickyHeightElems = this.parentElement.dataset.stickyHeightElems || '';
      if (this.stickyHeightElems) {
        window.addEventListener('resize', this.stickyHeightChangeHandler);

        if ('MutationObserver' in window) {
          this.mutationObserver = new MutationObserver(this.stickyHeightChangeHandler);
          document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
            this.mutationObserver.observe(elem, {
              childList: true,
              attributes: true,
              subtree: true
            });
          });
        }

        this.setStickyHeight();
      }

      // Listen for the opening of any disclosures, so their contents can be scrolled to if needed.
      this.disclosures = Array.from(this.querySelectorAll('details'));
      this.disclosures.forEach((disclosure) => {
        disclosure.addEventListener('transitionend', this.disclosureChangeHandler);
      });

      this.updateScrollPosition();
    }

    setInitialState() {
      const headerIsSticky = document.querySelector('store-header[data-is-sticky="true"]');
      this.headerHeight = Number.parseInt(
        getComputedStyle(this.parentElement).getPropertyValue('--header-height').replace('px', ''),
        10
      );
      this.currentTop = Number.parseInt(
        document.documentElement.scrollTop + this.getBoundingClientRect().top - this.offsetTop,
        10
      );
      this.defaultTop = headerIsSticky ? parseInt(this.headerHeight + 30, 10) : 48;
      this.scrollY = window.scrollY;
    }

    /**
     * Handle the opening/closing of disclosures when they extend beyond the viewport.
     * @param {object} evt - Event object.
     */
    handleDisclosureChange(evt) {
      if (!evt.target.classList.contains('disclosure__panel')) return;

      const disclosure = evt.target.closest('.disclosure');
      const summaryElem = disclosure ? disclosure.querySelector('summary') : null;
      if (summaryElem && !theme.elementUtil.isInViewport(summaryElem)) {
        window.scrollTo({
          behavior: 'smooth',
          top: summaryElem.getBoundingClientRect().top
            - document.body.getBoundingClientRect().top - this.headerHeight - 20
        });
      }
    }

    /**
     * Destroy listeners, observer and pending work, then reset the css top attribute.
     */
    removeListeners() {
      if (this.container) this.container.style.top = '';

      window.removeEventListener('scroll', this.scrollListener);
      window.removeEventListener('resize', this.stickyHeightChangeHandler);

      if (this.scrollFrame !== null) {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }
      if (this.stickyHeightTimer !== null) {
        window.clearTimeout(this.stickyHeightTimer);
        this.stickyHeightTimer = null;
      }

      this.mutationObserver?.disconnect();
      this.mutationObserver = null;

      this.disclosures?.forEach((disclosure) => {
        disclosure.removeEventListener('transitionend', this.disclosureChangeHandler);
      });
      this.disclosures = [];
      this.active = false;
    }

    handleStickyHeightChange() {
      if (!this.active) return;

      if (this.stickyHeightTimer !== null) window.clearTimeout(this.stickyHeightTimer);
      this.stickyHeightTimer = window.setTimeout(() => {
        this.stickyHeightTimer = null;
        if (this.active) this.setStickyHeight();
      }, 500);
    }

    /**
     * Calculates the heights of certain elements that the height of the sticky element depends on.
     */
    setStickyHeight() {
      let totalHeight = 0;
      document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
        totalHeight += elem.getBoundingClientRect().height;
      });
      this.parentElement.style.setProperty('--sticky-height', `${parseInt(totalHeight, 10)}px`);
    }

    /**
     * Coalesces scroll events into one layout read/write per animation frame.
     */
    handleScroll() {
      if (!this.active || this.scrollFrame !== null) return;
      this.scrollFrame = window.requestAnimationFrame(this.updateScrollPositionHandler);
    }

    /**
     * Updates the current css top based on scroll direction.
     */
    updateScrollPosition() {
      this.scrollFrame = null;
      if (!this.active || !this.container) return;

      const nextScrollY = window.scrollY;
      const bounds = this.container.getBoundingClientRect();
      const maxTop = bounds.top + nextScrollY - this.container.offsetTop + this.defaultTop;
      const minTop = this.container.clientHeight - window.innerHeight;

      if (nextScrollY < this.scrollY) {
        this.currentTop -= nextScrollY - this.scrollY;
      } else {
        this.currentTop += this.scrollY - nextScrollY;
      }

      this.currentTop = Math.min(Math.max(this.currentTop, -minTop), maxTop, this.defaultTop);
      this.scrollY = nextScrollY;
      this.container.style.top = `${this.currentTop}px`;
    }
  }

  customElements.define('sticky-scroll-direction', StickyScrollDirection);
}
